import {
  Chip,
  ColorsV2,
  Flex,
  FieldHelperText,
  FieldLabel,
  IconInCircle,
  Spacer,
  Text,
  TextInput,
  spaces,
} from '@electricjs/arc';
import { PackageNameEnum } from '@electricjs/core_entity-client';
import { useTranslation } from 'react-i18next';

import { OrganizationPackageWithEntitlements } from '@/types/packages';

type PlanLicenseSectionProps = {
  plan: OrganizationPackageWithEntitlements;
  newCount: string;
  onCountChange: (value: string) => void;
  isValid: boolean;
  delta: number;
  showHeader?: boolean;
  succeededDelta?: number;
};

const PlanLicenseSection = ({
  plan,
  newCount,
  onCountChange,
  isValid,
  delta,
  showHeader = true,
  succeededDelta,
}: PlanLicenseSectionProps) => {
  const { t } = useTranslation('people');
  const used = (plan.licenseCount ?? 0) - (plan.availableLicenseCount ?? 0);
  const total = plan.licenseCount ?? 0;
  const isAtLimit = plan.availableLicenseCount === 0;
  const inputId = `add-licenses-input-${plan.id}`;
  const isPro = plan.package.name === PackageNameEnum.Pro;
  const planIcon = isPro ? 'user-shield' : 'user';
  const planIconIntent = isPro ? 'primary' : 'info';
  const planIconBackground = isPro
    ? ColorsV2.PRIMARY_LIGHTEST
    : ColorsV2.INFO_LIGHTEST;

  return (
    <Flex vertical>
      {showHeader && (
        <>
          <Flex justifyContent="space-between" alignItems="center" width="100%">
            <Flex alignItems="center" columnGap={spaces.space_xs_rem}>
              <IconInCircle
                icon={planIcon}
                intent={planIconIntent}
                background={planIconBackground}
                customIconSize="1.6rem"
                size="3.2rem"
              />
              <Text variant="label-large">{plan.package.displayName}</Text>
            </Flex>
            <Chip
              id={`plan-used-badge-${plan.id}`}
              variant="small"
              backgroundColor={
                isAtLimit
                  ? ColorsV2.WARNING_LIGHTEST
                  : ColorsV2.SUCCESS_LIGHTEST
              }
              textColor={
                isAtLimit ? ColorsV2.WARNING_DARKEST : ColorsV2.SUCCESS
              }>
              {t('addLicensesModal.usedBadge', { used, total })}
            </Chip>
          </Flex>
          <Spacer space={spaces.space_s} />
        </>
      )}

      {/* succeededDelta is undefined for plans that haven't succeeded yet; a number (the delta) for plans that have */}
      {succeededDelta !== undefined ? (
        <Flex
          width="100%"
          backgroundColor={ColorsV2.SUCCESS_LIGHTEST}
          borderRadius="0.8rem"
          px={spaces.space_s_rem}
          py={spaces.space_s_rem}>
          <Text variant="label-small" color={ColorsV2.SUCCESS}>
            {t('addLicensesModal.addedDelta', {
              count: succeededDelta,
              planName: plan.package.displayName,
            })}
          </Text>
        </Flex>
      ) : (
        <>
          <FieldLabel htmlFor={inputId}>
            {t('addLicensesModal.newTotalLabel')}
          </FieldLabel>
          <Spacer space={spaces.space_xs} />
          <TextInput
            id={inputId}
            type="number"
            value={newCount}
            min="0"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onCountChange(e.target.value)
            }
          />
          <FieldHelperText>
            {t('addLicensesModal.helperText', { current: total })}
          </FieldHelperText>

          {isValid && delta > 0 && (
            <>
              <Spacer space={spaces.space_xs} />
              <Flex
                width="100%"
                backgroundColor={
                  isPro ? ColorsV2.PRIMARY_LIGHTEST : ColorsV2.INFO_LIGHTEST
                }
                borderRadius="0.8rem"
                px={spaces.space_s_rem}
                py={spaces.space_s_rem}>
                <Text
                  variant="label-small"
                  color={isPro ? ColorsV2.PRIMARY : ColorsV2.INFO_DARK}>
                  {t('addLicensesModal.addingDelta', {
                    count: delta,
                    planName: plan.package.displayName,
                  })}
                </Text>
              </Flex>
            </>
          )}
        </>
      )}
    </Flex>
  );
};

export default PlanLicenseSection;
